<template>
  <span v-if="typeof textBlock === 'string'">
    {{ textBlock }}
  </span>
  <span
    v-else-if="
      textRender &&
      !hasRichText(textBlock) &&
      !highlights?.[textBlock.paragraphId ?? '']
    "
  >
    {{ textBlock.content }}
  </span>
  <component
    v-else
    :is="componentType"
    :textBlock="textBlock"
    :showComments
    :showBookmarks
    :language
    :bookmarked
    :comments
    :overrideFont
    @comment="(v?: string) => emit('comment', v)"
    @footnote="(v: string) => emit('footnote', v)"
    ref="renderComponent"
  />
</template>

<script setup lang="ts">
import { type TextBlock } from "~/src/novel/core";
import { hasRichText } from "~/src/novel/render";
import type { AvailableLanguages } from "~/src/api";

const { textBlock, textRender } = defineProps<{
  textBlock: TextBlock | string;
  // format is <paragraph_id> to <bookmark_id:position:offset>
  highlights?: Record<string, string>;
  textRender?: boolean;
  comments?: number;
  bookmarked?: boolean;
  language?: AvailableLanguages;

  overrideFont?: boolean;
  showComments?: boolean;
  showBookmarks?: boolean;
}>();

const emit = defineEmits<{
  (e: "comment", id?: string): void;
  (e: "footnote", id: string): void;
}>();

const renderComponent = useTemplateRef("renderComponent");

const componentType = computed(() => {
  if (typeof textBlock === "string") return "span";

  if (textRender && !hasRichText(textBlock)) return "span";
  if (textBlock.type === "text") {
    if (textRender) return "NovelRenderRichText";
    return "NovelRenderParagraph";
  }
  switch (textBlock.type) {
    case "blockquote":
      return "NovelRenderBlockquote";
    case "callout":
      return "NovelRenderCallout";
    case "verse":
      return "NovelRenderVerse";
    case "scene_break":
      return "NovelRenderSceneBreak";
    case "page_break":
      return "NovelRenderPageBreak";
    case "image":
      return "NovelRenderImage";
  }
  return "NovelRenderParagraph";
});
</script>

<style lang="postcss">
@import url("assets/css/novel/style.pcss");
@import url("assets/css/novel/format.pcss");
</style>
